import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CatalogSearch } from '@/components/estimates/CatalogSearch';
import { EstimateLineRow, LINE_GRID } from '@/components/estimates/EstimateLineRow';
import { formatCurrency } from '@/lib/format';
import { centsToNumber } from '@/lib/money';
import {
  customLine,
  draftFromCatalogItem,
  draftFromLine,
  labourLine,
  lineCents,
  moveLine,
  toSaveLine,
  validateLine,
  type DraftLine,
} from '@/lib/estimateLines';
import {
  InvoiceInvalidError,
  InvoiceRefusedError,
  useSaveInvoiceLines,
  type Invoice,
} from '@/routes/invoices/useInvoices';

/**
 * Editing a DRAFT invoice's lines (M2-10, the AC's "editable on the invoice
 * before sending, without altering the job"). The lines are the invoice's own
 * copies — the job's stay as they were — so this saves to the invoice alone.
 *
 * <b>An explicit Save, not the estimate builder's autosave.</b> The invoice
 * page also carries "Mark as sent", and sending prices the SAVED lines: an
 * autosave half a second behind the last keystroke would let a send freeze the
 * version before it. Here the acts are hidden while the lines are open
 * (`InvoiceDetail`), and Save is the one way out that keeps the edit — so what
 * gets sent is always what was saved. Cancel puts the lines back as stored.
 *
 * The rows are the estimate builder's (`EstimateLineRow`, the price-book
 * search, custom and labour lines, reordering, discounts) — one line editor
 * for the product, not two. The draft shows no tax (M2-11): it is worked out
 * when the invoice is sent, at that day's rate.
 */
export function InvoiceLineEditor({
  invoice,
  onDone,
}: {
  invoice: Invoice;
  /** Called with whether the lines were saved (true) or the edit was cancelled (false). */
  onDone: (saved: boolean) => void;
}) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<DraftLine[]>(() => invoice.lines.map(draftFromLine));
  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set(lines.map((l) => l.key)));
  const searchRef = useRef<HTMLInputElement>(null);
  const save = useSaveInvoiceLines(invoice.id);

  const valid = lines.every((line) => validateLine(line) === null);
  const totals = lines.map(lineCents);
  const subtotal = valid ? totals.reduce((sum, c) => sum + c!.total, 0n) : null;
  const problem =
    save.error instanceof InvoiceRefusedError || save.error instanceof InvoiceInvalidError
      ? t('invoices.edit.refused', { reason: save.error.message })
      : save.isError
        ? t('invoices.edit.failed')
        : null;

  function submit() {
    // Every line shows its errors once Save is pressed, visited or not.
    setVisited(new Set(lines.map((l) => l.key)));
    if (!valid) return;
    save.mutate(lines.map(toSaveLine), { onSuccess: () => onDone(true) });
  }

  return (
    <section aria-labelledby="invoice-edit-heading" className="space-y-4">
      <div className="space-y-1">
        <h2 id="invoice-edit-heading" className="text-base font-semibold">
          {t('invoices.edit.heading')}
        </h2>
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('invoices.edit.help')}</p>
      </div>

      <CatalogSearch
        ref={searchRef}
        onAdd={(item) => setLines((current) => [...current, draftFromCatalogItem(item)])}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setLines((current) => [...current, customLine()])}
        >
          {t('estimates.builder.addCustom')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            setLines((current) => [
              ...current,
              labourLine(t('estimates.builder.labourDescription'), null),
            ])
          }
        >
          {t('estimates.builder.addLabour')}
        </Button>
      </div>

      <div className="space-y-3" data-testid="invoice-edit-lines">
        {lines.length > 0 ? (
          <div
            aria-hidden="true"
            className={`hidden gap-2 text-xs font-medium text-[var(--color-muted-foreground)] md:grid ${LINE_GRID}`}
          >
            <span>{t('estimates.line.description')}</span>
            <span className="text-right">{t('estimates.line.quantity')}</span>
            <span className="text-right">{t('estimates.line.unitPrice')}</span>
            <span>{t('estimates.line.taxCategory')}</span>
            <span>{t('estimates.line.discount')}</span>
            <span className="text-right">{t('estimates.line.total')}</span>
            <span />
          </div>
        ) : (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('estimates.builder.empty')}
          </p>
        )}
        {lines.map((line, index) => (
          <div
            key={line.key}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setVisited((current) => new Set(current).add(line.key));
              }
            }}
          >
            <EstimateLineRow
              line={line}
              index={index}
              count={lines.length}
              showErrors={visited.has(line.key)}
              onChange={(next) =>
                setLines((current) => current.map((l) => (l.key === line.key ? next : l)))
              }
              onMove={(by) => setLines((current) => moveLine(current, index, by))}
              onRemove={() => {
                setLines((current) => current.filter((l) => l.key !== line.key));
                searchRef.current?.focus();
              }}
            />
          </div>
        ))}
      </div>

      <dl className="ml-auto grid max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
        <dt>{t('invoices.totals.subtotal')}</dt>
        <dd className="text-right tabular-nums" data-testid="edit-subtotal">
          {subtotal === null ? '—' : formatCurrency(centsToNumber(subtotal))}
        </dd>
        <dt>{t('invoices.totals.tax')}</dt>
        <dd className="text-right text-[var(--color-muted-foreground)]">
          {t('invoices.totals.taxAtSend')}
        </dd>
      </dl>

      {!valid ? (
        <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="edit-invalid">
          {t('estimates.builder.invalid')}
        </p>
      ) : null}
      {problem ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="button" disabled={save.isPending} onClick={submit}>
          {save.isPending ? t('invoices.edit.saving') : t('invoices.edit.save')}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={save.isPending}
          onClick={() => onDone(false)}
        >
          {t('invoices.edit.cancel')}
        </Button>
      </div>
    </section>
  );
}
